import { DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAppDispatch, usePlayer } from '@/hooks/redux';
import { fetchPlayers, updatePlayerStatus } from '@/store/slices/playerSlice';
import { Player } from '@/types';
import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PlayerList = () => {
  const { players, total, loading } = usePlayer();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');

  const udpatedPlayers = players.map((item) => ({
    ...item,
    username: item.username ?? `${item.firstname} ${item.lastname}`,
  }));

  useEffect(() => {
    dispatch(
      fetchPlayers({
        page: pageIndex + 1,
        limit: pageSize,
        search,
      })
    );
  }, [pageIndex, search]);

  const columns: ColumnDef<Player>[] = [
    { accessorKey: 'id', header: 'Player ID' },
    { accessorKey: 'username', header: 'Username/Name' },
    { accessorKey: 'mobile', header: 'Mobile' },
    { accessorKey: 'email', header: 'Email' },

    {
      accessorKey: 'wallet',
      header: 'Wallet',
      cell: ({ row }) => `₹${row.original.wallet?.amount ?? 0}`,
    },

    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <span className={row.original.status === 'Active' ? 'text-green-500' : 'text-red-500'}>
          {row.original.status}
        </span>
      ),
    },

    {
      accessorKey: 'lastLogin',
      header: 'Last Login',
      cell: ({ row }) =>
        row.original.lastLogin ? new Date(row.original.lastLogin).toLocaleString() : '—',
    },

    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const player = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/players/profile/${player.id}`)}>
                View
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={async () => {
                  await dispatch(
                    updatePlayerStatus({
                      id: player.id,
                      status: player.status === 'Active' ? 'Blocked' : 'Active',
                    })
                  );
                  dispatch(fetchPlayers({ page: pageIndex, search }));
                }}
              >
                {player.status === 'Active' ? 'Block' : 'Unblock'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold">Player Management</h2>
          <p className="text-muted-foreground">Manage players dynamically</p>
        </div>
      </div>

      <DataTable
        data={udpatedPlayers}
        columns={columns}
        searchPlaceholder="Search players..."
        pageIndex={pageIndex}
        pageSize={pageSize}
        total={total}
        onPageChange={setPageIndex}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />
    </>
  );
};

export default PlayerList;
